import { useEffect, useRef, useState } from "react";
import { CommonCard } from "@/features/ProductList/components/CommonCard/CommonCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { ViewProps } from "../../types";

export const PopupList = ({
  products,
  handleImageClick,
  show,
  productSize,
}: ViewProps) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768); // typical mobile breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleScroll = () => {
    const scrollContainer = ref.current;
    if (scrollContainer) {
      // More precise scroll position check
      const isAtStart = Math.abs(scrollContainer.scrollLeft) < 1;
      const isAtEnd =
        Math.abs(
          scrollContainer.scrollWidth -
            scrollContainer.clientWidth -
            scrollContainer.scrollLeft
        ) < 1;

      setCanScrollLeft(!isAtStart);
      setCanScrollRight(!isAtEnd);
    }
  };

  useEffect(() => {
    const scrollContainer = ref.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      // Initial check
      handleScroll();

      // Add resize observer to handle container size changes
      const resizeObserver = new ResizeObserver(() => {
        handleScroll();
      });
      resizeObserver.observe(scrollContainer);

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, []);

  const scroll = (direction: "left" | "right") => {
    const scrollContainer = ref.current;
    if (scrollContainer) {
      scrollContainer.scrollBy({
        left: direction === "left" ? -500 : 500,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="w-full relative">
      {canScrollLeft && (
        <button
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 p-2 rounded-r-lg shadow-md transition-opacity"
          onClick={() => scroll("left")}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
      )}
      {canScrollRight && (
        <button
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 p-2 rounded-l-lg shadow-md transition-opacity"
          onClick={() => scroll("right")}
        >
          <FontAwesomeIcon icon={faArrowRight} />
        </button>
      )}
      <div
        ref={ref}
        className="w-full lg:px-5 lg:p-4 pt-4 pb-4 flex gap-4 overflow-x-auto snap-x snap-mandatory lg:scroll-smooth"
      >
        {products?.data?.map((item, index) => (
          <div className="snap-center" key={index}>
            <CommonCard
              imageSource={
                item.attributes?.media?.data?.[0]?.attributes?.formats?.large
                  ?.url
              }
              imageAlt={
                item.attributes?.media?.data?.[0]?.attributes?.alternativeText
              }
              title={item.attributes?.title}
              category={item.attributes?.category?.data?.attributes?.name}
              description={item.attributes?.description}
              price={item.attributes?.price}
              weight={item.attributes?.weight}
              calories={item.attributes?.total_calories}
              nutritionalInfo={item.attributes?.nutritional_information}
              ingredients={
                item?.attributes?.ingredients?.data?.map(
                  (ingredient) => ingredient?.attributes?.name
                ) as string[]
              }
              allergens={
                item?.attributes?.ingredients?.data?.flatMap((ingredient) =>
                  ingredient?.attributes?.allergens?.data?.map(
                    (allergen) => allergen?.attributes?.name
                  )
                ) as string[]
              }
              show={show}
              onClick={() => handleImageClick(index)}
              productSize={productSize}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
